// Единые расчёты грузовых параметров для форм и карточек груза.

const DIMENSION_FIELDS_M = ["heightM", "lengthM", "widthM"];
const CARGO_MODEL_DIMENSION_FIELDS_M = ["height_m", "length_m", "width_m"];
const VOLUME_FIELD_M3 = "volumeM3";
const CARGO_MODEL_VOLUME_FIELD_M3 = "volume_m3";

// Приводит значение из формы к положительному числу для расчётов груза.
export function cargoNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  const normalized = String(value).replace(/\s/g, "").replace(",", ".");
  const number = Number(normalized);
  if (!Number.isFinite(number) || number <= 0) return null;

  return number;
}

// Округляет общий объём ЭЗЗ до двух знаков, потому что XSD 1110361 принимает
// `Объем` с fractionDigits=2.
export function roundCargoVolume(value) {
  return Math.round(value * 100) / 100;
}

// Считает объём ЭЗЗ в м3 по габаритам места.
// Если количество мест ещё не заполнено, считаем объём одного места: это не
// отменяет обязательность количества, а только помогает пользователю заполнить форму.
export function volumeM3FromMeterDimensions({ heightM, lengthM, widthM, quantityPlaces = 1 }) {
  const height = cargoNumber(heightM);
  const length = cargoNumber(lengthM);
  const width = cargoNumber(widthM);
  const quantity = cargoNumber(quantityPlaces) || 1;

  if (!height || !length || !width) return null;

  return roundCargoVolume(height * length * width * quantity);
}

// Считает объём одной позиции груза в м3 по габаритам в сантиметрах.
export function volumeM3FromCentimeterDimensions({ heightCm, lengthCm, widthCm }) {
  const height = cargoNumber(heightCm);
  const length = cargoNumber(lengthCm);
  const width = cargoNumber(widthCm);

  if (!height || !length || !width) return null;

  const volume = (height * length * width) / 1_000_000;
  return Math.round(volume * 1_000_000) / 1_000_000;
}

// Нормализует габарит cargo-модели в метры: новые формы работают с `*_m`,
// а старые ответы и legacy-формы могут прислать только сохраненные `*_cm`.
export function cargoModelDimensionM(item, dimension) {
  const meterValue = cargoNumber(item?.[`${dimension}_m`]);
  if (meterValue) return meterValue;

  const centimeterValue = cargoNumber(item?.[`${dimension}_cm`]);
  if (!centimeterValue) return "";

  return centimeterValue / 100;
}

// Считает общий объём cargo-позиции для ЭДО из объёма одного места.
export function totalVolumeM3FromCargoModelItem(item) {
  const unitVolume = cargoNumber(item?.volume_m3);
  const quantity = cargoNumber(item?.quantity) || 1;

  if (!unitVolume) return null;

  return roundCargoVolume(unitVolume * quantity);
}

// Проверяет, относится ли поле формы к габаритам груза в метрах.
export function isDimensionFieldM(field) {
  return DIMENSION_FIELDS_M.includes(field);
}

// Проверяет, может ли изменение поля повлиять на автоподсчёт объёма ЭЗЗ.
export function shouldRecalculateCargoVolume(field) {
  return isDimensionFieldM(field) || field === "quantityPlaces";
}

// Проверяет, может ли изменение поля означать ручной ввод общего объёма.
export function isVolumeFieldM3(field) {
  return field === VOLUME_FIELD_M3;
}

// Проверяет, может ли изменение поля повлиять на автоподсчёт объёма cargo-модели.
export function shouldRecalculateCargoModelVolume(field) {
  return CARGO_MODEL_DIMENSION_FIELDS_M.includes(field);
}

// Автоматически заполняет объём ЭЗЗ из габаритов, но не перетирает значение,
// которое пользователь уже указал руками.
export function syncCargoVolumeFromDimensions(item, changedField) {
  if (!shouldRecalculateCargoVolume(changedField)) return false;
  if (!DIMENSION_FIELDS_M.every((field) => Object.prototype.hasOwnProperty.call(item, field))) return false;

  const volume = volumeM3FromMeterDimensions({
    heightM: item.heightM,
    lengthM: item.lengthM,
    widthM: item.widthM,
    quantityPlaces: item.quantityPlaces,
  });
  if (volume === null) return false;

  const currentVolume = cargoNumber(item.volumeM3);
  const previousAutoVolume = cargoNumber(item.autoCalculatedVolumeM3);
  if (currentVolume && previousAutoVolume && currentVolume !== previousAutoVolume) return false;

  item.volumeM3 = volume;
  item.autoCalculatedVolumeM3 = volume;
  return true;
}

// Очищает габариты, когда пользователь вручную вводит объём, который не равен
// расчёту по В × Ш × Д × количеству мест. Иначе в ЭДО-форме остаётся
// противоречие: заполненные габариты говорят об одном объёме, а ручной объём о другом.
export function syncCargoDimensionsFromManualVolume(item, changedField) {
  if (!isVolumeFieldM3(changedField)) return false;
  if (!DIMENSION_FIELDS_M.every((field) => Object.prototype.hasOwnProperty.call(item, field))) return false;

  const manualVolume = cargoNumber(item.volumeM3);
  if (!manualVolume) return false;

  const calculatedVolume = volumeM3FromMeterDimensions({
    heightM: item.heightM,
    lengthM: item.lengthM,
    widthM: item.widthM,
    quantityPlaces: item.quantityPlaces,
  });
  if (calculatedVolume === null || manualVolume === calculatedVolume) return false;

  DIMENSION_FIELDS_M.forEach((field) => {
    item[field] = null;
  });
  item.autoCalculatedVolumeM3 = null;
  return true;
}

// Автоматически заполняет объём одной единицы cargo-модели из габаритов в м.
export function syncCargoModelVolumeFromDimensions(item, changedField) {
  if (!shouldRecalculateCargoModelVolume(changedField)) return false;
  if (!CARGO_MODEL_DIMENSION_FIELDS_M.every((field) => Object.prototype.hasOwnProperty.call(item, field))) return false;

  const volume = volumeM3FromMeterDimensions({
    heightM: item.height_m,
    lengthM: item.length_m,
    widthM: item.width_m,
  });
  if (volume === null) return false;

  const currentVolume = cargoNumber(item.volume_m3);
  const previousAutoVolume = cargoNumber(item.auto_calculated_volume_m3);
  if (currentVolume && previousAutoVolume && currentVolume !== previousAutoVolume) return false;

  item.volume_m3 = volume;
  item.auto_calculated_volume_m3 = volume;
  return true;
}

// Очищает габариты cargo-модели, если пользователь руками ввёл другой объём.
export function syncCargoModelDimensionsFromManualVolume(item, changedField) {
  if (changedField !== CARGO_MODEL_VOLUME_FIELD_M3) return false;
  if (!CARGO_MODEL_DIMENSION_FIELDS_M.every((field) => Object.prototype.hasOwnProperty.call(item, field))) return false;

  const manualVolume = cargoNumber(item.volume_m3);
  if (!manualVolume) return false;

  const calculatedVolume = volumeM3FromMeterDimensions({
    heightM: item.height_m,
    lengthM: item.length_m,
    widthM: item.width_m,
  });
  if (calculatedVolume === null || manualVolume === calculatedVolume) return false;

  CARGO_MODEL_DIMENSION_FIELDS_M.forEach((field) => {
    item[field] = null;
  });
  item.auto_calculated_volume_m3 = null;
  return true;
}

// Считает массу брутто для ЭДО из количества мест и веса одного места.
export function grossWeightKgFromCargoItem({ quantity, weight_kg: weightKg }) {
  const places = cargoNumber(quantity);
  const weight = cargoNumber(weightKg);

  if (!places || !weight) return null;

  return Number((places * weight).toFixed(3));
}
